import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';

interface PlateBox {
  xmin: number;
  ymin: number;
  xmax: number;
  ymax: number;
}

/**
 * Renders a cropped preview of the recognized number plate.
 *
 * It reads the stored Plate Recognizer response (anprJson) plus the chosen
 * result index, takes the box of the selected result and crops that area out
 * of the original image using a pure-CSS background crop (no canvas, no CORS).
 *
 * The box coordinates are relative to the image the recognition ran on
 * (anpr_json.image_width / image_height). The same coordinates drive the green
 * box overlay, so the crop stays aligned with the image without any canvas
 * export that could fail on a tainted (non-CORS) image.
 */
@Component({
  selector: 'app-plate-crop-preview',
  templateUrl: './plate-crop-preview.component.html',
  styleUrls: ['./plate-crop-preview.component.scss'],
})
export class PlateCropPreviewComponent implements OnChanges {
  /** full Plate Recognizer response as stored on the record (anpr_json) */
  @Input() anprJson: string | null | undefined;

  /** index of the chosen result inside results[] (anpr_selected_index) */
  @Input() selectedIndex: number | null | undefined;

  /** base url for images, e.g. environment.serverUrl + '/' */
  @Input() imageStorageBase = '';

  /**
   * Optional explicit source image filename (as stored in tbl_images / Fileupload).
   * When omitted we derive the source file from anpr_json.camera_id, because the
   * Plate Recognizer "filename" is the service's own internal name and does NOT
   * exist in our storage.
   */
  @Input() imageFilename: string | null | undefined;

  /** stored country code, e.g. kzland / Md_KzLand (A, D, SK, H, ...) */
  @Input() land: string | null | undefined;

  /** stored plate number, e.g. kznummer / Md_KzNummer */
  @Input() plateNumber: string | null | undefined;

  /**
   * Optional list of district codes (Bezirkskennzeichen) the client actually uses.
   * When provided, the plate split prefers these codes, which disambiguates plates
   * like "WELVG17" (W-ELVG17 vs WE-LVG17). Falls back to the full Austrian list.
   */
  @Input() bezirkeCodes: string[] | null | undefined;

  detectedPlate = '';

  /** fallback CSS plate badge model, used when no crop is available */
  badge: { country: string; district: string; number: string } | null = null;

  private parsed: any = null;
  private box: PlateBox | null = null;

  /**
   * Austrian district codes (Bezirkskennzeichen).
   * Source of truth: api/src/Utils/TransferCheck.php -> $BEZIRKE_AUSTRIA.
   * Keep in sync with that list.
   */
  private static readonly BEZIRKE_AUSTRIA: string[] = [
    'A', 'AM', 'B', 'BA', 'BB', 'BD', 'BG', 'BH', 'BK', 'BL', 'BM', 'BN', 'BP', 'BR', 'BZ',
    'DL', 'DO', 'E', 'EF', 'EU', 'FB', 'FE', 'FF', 'FK', 'FR', 'FV', 'FW', 'G', 'GB', 'GD', 'GF', 'GK', 'GM', 'GR', 'GS',
    'GU', 'HA', 'HB', 'HE', 'HF', 'HL', 'HO', 'I', 'IL', 'IM', 'JE', 'JO', 'JU', 'JW', 'K', 'KB', 'KD', 'KF', 'KG', 'KI',
    'KK', 'KL', 'KO', 'KR', 'KS', 'KU', 'L', 'LA', 'LB', 'LD', 'LE', 'LF', 'LI', 'LK', 'LL', 'LN', 'LZ', 'MA', 'MD', 'ME', 'MI',
    'MT', 'MU', 'MZ', 'N', 'ND', 'NK', 'O', 'OP', 'OW', 'P', 'PE', 'PL', 'PT', 'RA', 'RE', 'RI', 'RO',
    'S', 'SB', 'SD', 'SE', 'SK', 'SL', 'SO', 'SP', 'SR', 'ST', 'SV', 'SW', 'SZ', 'T', 'TA', 'TD', 'TK', 'TU', 'UU',
    'V', 'VB', 'VD', 'VI', 'VK', 'VL', 'VO', 'W', 'WB', 'WD', 'WE', 'WK', 'WL', 'WN', 'WO', 'WT', 'WU', 'WY', 'WZ',
    'ZE', 'ZT', 'ZW',
  ];

  ngOnChanges(_changes: SimpleChanges): void {
    this.reset();
    this.prepare();
    this.buildBadge();
  }

  private reset(): void {
    this.detectedPlate = '';
    this.parsed = null;
    this.box = null;
    this.badge = null;
  }

  private prepare(): void {
    if (!this.anprJson) {
      return;
    }

    try {
      this.parsed = typeof this.anprJson === 'string' ? JSON.parse(this.anprJson) : this.anprJson;
    } catch (err) {
      console.warn('[PlateCropPreview] could not parse anpr_json', err);
      return;
    }

    const results = this.parsed?.results;
    if (!Array.isArray(results) || results.length === 0) {
      return;
    }

    const result = this.pickResult(results);
    if (!result || !result.box) {
      return;
    }

    this.detectedPlate = ('' + (result.plate ?? '')).toUpperCase();
    this.box = result.box as PlateBox;
  }

  /**
   * CSS background-crop style for the recognized plate. Uses the box and the
   * recognition image size (image_width / image_height) - the same source the
   * green box overlay trusts - and renders the crop as a scaled background
   * image. No canvas, no crossorigin, no toDataURL, so it renders wherever the
   * green box renders. Returns null when there is no usable ANPR crop.
   */
  get cropStyle(): { [k: string]: string } | null {
    if (!this.box) {
      return null;
    }

    const w = Number(this.parsed?.image_width);
    const h = Number(this.parsed?.image_height);
    const filename = this.resolveSourceFilename();
    if (!w || !h || !filename) {
      return null;
    }

    const boxW = this.box.xmax - this.box.xmin;
    const boxH = this.box.ymax - this.box.ymin;
    if (boxW <= 0 || boxH <= 0) {
      return null;
    }

    // a little context around the plate
    const padX = boxW * 0.12;
    const padY = boxH * 0.35;
    const cropX = Math.max(0, this.box.xmin - padX);
    const cropY = Math.max(0, this.box.ymin - padY);
    const cropW = Math.min(w - cropX, boxW + padX * 2);
    const cropH = Math.min(h - cropY, boxH + padY * 2);

    const targetH = 112; // matches the previous .plate-image height
    const scale = targetH / cropH;
    const url = this.imageStorageBase + 'img?f=' + filename;

    return {
      width: cropW * scale + 'px',
      height: cropH * scale + 'px',
      'background-image': `url("${url}")`,
      'background-repeat': 'no-repeat',
      'background-size': w * scale + 'px ' + h * scale + 'px',
      'background-position': `-${cropX * scale}px -${cropY * scale}px`,
    };
  }

  /**
   * Resolves the filename of the image the recognition actually ran on, as it is
   * stored in our system.
   *
   * Order of preference:
   *  1. explicit imageFilename input from the parent
   *  2. basename of anpr_json.camera_id (e.g.
   *     "Flughafen-1red - /var/.../storage/2026/06/18/zJbJ91000005167.jpg")
   *  3. anpr_json.filename (Plate Recognizer's internal name - usually NOT a
   *     valid local file, kept only as a last resort)
   */
  private resolveSourceFilename(): string | null {
    if (this.imageFilename) {
      return ('' + this.imageFilename).trim();
    }

    const cameraId = this.parsed?.camera_id;
    if (typeof cameraId === 'string' && cameraId.length > 0) {
      const pathPart = cameraId.split(' - ').pop() ?? cameraId;
      const base = pathPart.split(/[\\/]/).pop();
      if (base && base.trim().length > 0) {
        return base.trim();
      }
    }

    return this.parsed?.filename ?? null;
  }

  /**
   * Builds the fallback CSS plate badge from the stored plate fields.
   * For Austrian plates the district code is split off so the dot/Wappen
   * placeholder can sit between district and number.
   */
  private buildBadge(): void {
    const normalized = this.normalizePlate(this.plateNumber);
    if (!normalized) {
      this.badge = null;
      return;
    }

    const country = (this.land || 'A').toUpperCase().trim();

    if (country === 'A') {
      const split = this.separatePlate(normalized);
      if (split) {
        this.badge = { country: 'A', district: split.bezirk, number: split.rest };
      } else {
        this.badge = { country: 'A', district: '', number: normalized };
      }
    } else {
      this.badge = { country, district: '', number: normalized };
    }
  }

  private normalizePlate(plate: string | null | undefined): string {
    if (!plate) {
      return '';
    }
    return ('' + plate)
      .replace(/[ .]/g, '')
      .replace(/[^a-zA-Z0-9-]/g, '')
      .toUpperCase();
  }

  /**
   * Splits an Austrian plate into its district code and the remainder using
   * the longest matching prefix. Returns null when no district matches.
   * Port of separatePlate() in api/src/Utils/TransferCheck.php.
   */
  private separatePlate(plate: string): { bezirk: string; rest: string } | null {
    // prefer the client's curated district codes when available, otherwise the
    // full Austrian list. Longest match first.
    const source =
      this.bezirkeCodes && this.bezirkeCodes.length > 0
        ? this.bezirkeCodes.map((c) => ('' + c).toUpperCase().trim()).filter((c) => c.length > 0)
        : PlateCropPreviewComponent.BEZIRKE_AUSTRIA;

    const bezirke = [...source].sort((a, b) => b.length - a.length);
    for (const bezirk of bezirke) {
      if (plate.indexOf(bezirk) === 0) {
        return { bezirk, rest: plate.substring(bezirk.length) };
      }
    }
    return null;
  }

  private pickResult(results: any[]): any {
    const idx = Number(this.selectedIndex);
    if (Number.isInteger(idx) && idx >= 0 && idx < results.length) {
      return results[idx];
    }
    return results[0];
  }
}
